/**
 * AUTO-GIFT NO CARRINHO - Shopify Ajax Cart API
 * -------------------------------------------------
 * Adiciona automaticamente um produto-gift quando o cliente tem
 * mais de X itens no carrinho. Remove automaticamente se cair
 * abaixo do threshold.
 *
 * PRÉ-REQUISITOS (fazer antes de usar este script):
 * 1. Crie um produto "Gift - Free Sample" (ou nome que preferir),
 *    com uma variante única. Esconda da navegação/coleções
 *    (não listar em nenhuma coleção, remover de sitemap se quiser).
 * 2. Pegue o VARIANT ID dessa variante (Admin > Products > o produto
 *    > clique na variante > o ID aparece na URL).
 * 3. Crie um desconto automático "Buy X Get Y" no Admin > Discounts:
 *    - Customer buys: mínimo de itens = THRESHOLD (ver abaixo)
 *    - Customer gets: esse produto gift, 100% off
 *    - Sem código, tipo "Automatic discount"
 *    Isso garante que o preço vá pra R$0 quando o gift entrar no carrinho.
 *
 * ONDE COLOCAR:
 * Cole este arquivo como um asset (ex: assets/auto-gift.js) e
 * carregue com <script src="{{ 'auto-gift.js' | asset_url }}" defer></script>
 * no theme.liquid, antes do </body>.
 */

(function () {
  'use strict';

  // ======= CONFIGURAÇÃO — AJUSTE AQUI =======
  const CONFIG = {
    GIFT_VARIANT_ID: 57933821444440, // variant ID do óculos (gift) - Leopardo / Tamanho Único
    THRESHOLD: 2, // a partir de 2 peças (2, 3, 4...) já ganha o gift
    GIFT_QUANTITY: 1, // quantas unidades do gift adicionar
  };
  // ============================================

  let isProcessing = false; // trava contra loop/duplo disparo
  let ourOwnRequestInFlight = false; // trava contra o fallback re-disparar por causa da NOSSA própria chamada
  let lastAddFailedAt = 0; // timestamp da última falha, pra não martelar a API
  const RETRY_COOLDOWN_MS = 15000; // espera 15s antes de tentar de novo depois de um erro

  // Verifica se uma line item do carrinho é o nosso gift
  function isGiftItem(item) {
    return item.variant_id === CONFIG.GIFT_VARIANT_ID;
  }

  // Conta itens do carrinho EXCLUINDO o gift (pra não contar ele mesmo)
  function countNonGiftItems(cart) {
    return cart.items
      .filter((item) => !isGiftItem(item))
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function addGift() {
    ourOwnRequestInFlight = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: CONFIG.GIFT_VARIANT_ID,
              quantity: CONFIG.GIFT_QUANTITY,
              properties: { _gift: 'true' }, // marca o item como gift (auditoria/CSS)
            },
          ],
        }),
      });

      if (!res.ok) {
        // Loga o motivo REAL que a Shopify devolveu (ex: "This item requires a
        // selling plan", "Not enough stock", regra de quantidade, etc.)
        const errorBody = await res.json().catch(() => null);
        console.error(
          '[auto-gift] Shopify recusou adicionar o gift (status ' + res.status + '):',
          errorBody
        );
        lastAddFailedAt = Date.now();
      }

      return res;
    } finally {
      // pequeno delay antes de liberar a trava, pra deixar nosso próprio
      // fetch() já ter dado o "return" no interceptor lá embaixo
      setTimeout(() => {
        ourOwnRequestInFlight = false;
      }, 300);
    }
  }

  async function removeGift(cart) {
    const giftLine = cart.items.find(isGiftItem);
    if (!giftLine) return;

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: giftLine.key,
        quantity: 0,
      }),
    });
  }

  async function syncGift() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await getCart();
      const nonGiftCount = countNonGiftItems(cart);
      const hasGift = cart.items.some(isGiftItem);

      const inCooldown = Date.now() - lastAddFailedAt < RETRY_COOLDOWN_MS;

      if (nonGiftCount >= CONFIG.THRESHOLD && !hasGift && !inCooldown) {
        await addGift();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else if (nonGiftCount < CONFIG.THRESHOLD && hasGift) {
        const freshCart = await getCart(); // recarrega pra pegar o key certo
        await removeGift(freshCart);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (err) {
      console.error('[auto-gift] erro ao sincronizar:', err);
    } finally {
      isProcessing = false;
    }
  }

  // ======= GATILHOS =======
  // Dawn e a maioria dos temas modernos disparam "cart:updated" via
  // eventos customizados no documento. Ajuste os nomes de evento
  // conforme seu tema, se necessário (ex: Kaching, UpCart podem usar
  // nomes próprios — verificar no console do navegador).

  document.addEventListener('cart:updated', syncGift);
  document.addEventListener('cart:refresh', syncGift, { once: false });

  // Fallback: intercepta fetch para pegar respostas de /cart/add.js e
  // /cart/change.js de qualquer parte do tema (cobre temas que não
  // disparam eventos customizados).
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    const isCartMutation =
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update');

    const promise = originalFetch.apply(this, args);

    if (isCartMutation && !ourOwnRequestInFlight) {
      promise.then(() => {
        // pequeno delay pra garantir que o cart.js já reflete a mudança
        setTimeout(syncGift, 150);
      });
    }

    return promise;
  };

  // Roda uma vez no load, caso o cliente chegue com carrinho já cheio
  document.addEventListener('DOMContentLoaded', syncGift);
})();